'use client';

import { useEffect, useRef } from 'react';
import type { ReactNode, RefObject } from 'react';
import Link from 'next/link';
import './tax.css';

/* Saari styling tax.css mein hai.
   Hero + Final CTA background image ka path CSS mein hai:
   tax.css -> ".tax-hero-bg-image" aur ".final-cta-bg"

   Image (public/assets/images/ mein daalo):
   - knowledgehero.png -> Hero background + Final CTA background */

type IconName =
  | 'transactions'
  | 'accounting'
  | 'tax'
  | 'reconcile'
  | 'controls'
  | 'consolidation'
  | 'disclosure'
  | 'filing'
  | 'audit'
  | 'users'
  | 'rupee'
  | 'resilient'
  | 'clock'
  | 'trend'
  | 'data'
  | 'alert'
  | 'check'
  | 'evidence';

function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, ReactNode> = {
    transactions: <><path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" /><path d="M9 8h6M9 12h6" /></>,
    accounting: <><path d="M5 4h11a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3z" /><path d="M5 17a3 3 0 0 1 3-3h11" /></>,
    tax: <><path d="M19 5 5 19" /><circle cx="7" cy="7" r="2.5" /><circle cx="17" cy="17" r="2.5" /></>,
    reconcile: <><path d="M4 9a8 8 0 0 1 14-3l2 2M20 15a8 8 0 0 1-14 3l-2-2" /><path d="M20 4v4h-4M4 20v-4h4" /></>,
    controls: <><path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6z" /><path d="m9 12 2 2 4-4" /></>,
    consolidation: <><path d="M12 3 3 8l9 5 9-5z" /><path d="m3 12 9 5 9-5" /><path d="m3 16 9 5 9-5" /></>,
    disclosure: <><path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5M10 13h6M10 17h6" /></>,
    filing: <><path d="M4 12 20 4l-4 16-4-6z" /><path d="m12 14 8-10" /></>,
    audit: <><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5" /><path d="m8 10.5 2 2 3-3.5" /></>,
    users: <><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" /><path d="M16 5a3 3 0 0 1 0 6M18 14c1.8.8 3 2.6 3 5" /></>,
    rupee: <><path d="M7 5h10M7 9h10M7 5c5 0 6 2 6 4s-2 4-6 4l7 6" /></>,
    resilient: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    trend: <><path d="M4 17 10 11l4 4 6-8" /><path d="M15 7h5v5" /></>,
    data: <><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M8 15v-3M12 15V8M16 15v-5" /></>,
    alert: <><path d="M12 4 3 20h18z" /><path d="M12 10v4M12 17h.01" /></>,
    check: <><circle cx="12" cy="12" r="9" /><path d="m8 12 2.5 2.5L16 9" /></>,
    evidence: <><path d="M4 5h16v4H4zM5 9v10h14V9M10 13h4" /></>,
  };

  return (
    <svg
      className="tax-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

function Arrow() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="M14 5l7 7m0 0l-7 7m7-7H3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
    </svg>
  );
}

/* ============================== DATA ============================== */

const questions = [
  'Where are our tax and transaction mismatches?',
  'Which transactions could create tax exposure?',
  'Are related-party transactions completely reconciled?',
  'What changed that should affect our disclosures?',
  'Can every disclosed number be traced to its source?',
  'Why does closing and consolidation take so long?',
  'Which transactions need investigation before audit or filing?',
  'Are we meeting changing regulatory requirements?',
  'How do we improve tax efficiency while staying compliant?',
  'Do we have the right controls and approvals in place?',
];

const flow: { icon: IconName; name: string; detail: string }[] = [
  { icon: 'transactions', name: 'Transactions', detail: 'Sales, Purchase, Finance, Treasury, Sub-ledgers' },
  { icon: 'accounting', name: 'Accounting', detail: 'GL, Accruals, Adjustments' },
  { icon: 'tax', name: 'Tax', detail: 'Direct Tax, Indirect Tax, Withholding, GST/VAT' },
  { icon: 'reconcile', name: 'Reconciliation', detail: 'Books vs Tax, Inter-entity, Related Party' },
  { icon: 'controls', name: 'Controls', detail: 'Rules, Approvals, Exceptions' },
  { icon: 'consolidation', name: 'Consolidation', detail: 'Entity Close, Eliminations, Adjustments' },
  { icon: 'disclosure', name: 'Disclosure', detail: 'Notes, Schedules, Statements' },
  { icon: 'filing', name: 'Filing / Reporting', detail: 'Regulatory Filings, Statutory Reports' },
  { icon: 'audit', name: 'Audit Trail', detail: 'Complete Traceability to Source' },
];

const outcomes: { icon: IconName; text: string }[] = [
  { icon: 'controls', text: 'Lower tax risk and penalties' },
  { icon: 'consolidation', text: 'Faster close and consolidation' },
  { icon: 'disclosure', text: 'Accurate and compliant disclosures' },
  { icon: 'audit', text: 'Complete traceability and audit readiness' },
  { icon: 'accounting', text: 'Stronger controls and governance' },
  { icon: 'rupee', text: 'Improved tax efficiency' },
  { icon: 'users', text: 'Higher stakeholder confidence' },
  { icon: 'resilient', text: 'A more resilient and transparent business' },
];

const intelligence: { icon: IconName; title: string; tags: string[] }[] = [
  { icon: 'tax', title: 'Tax Intelligence', tags: ['Transactions', 'Classifications', 'Exceptions', 'Exposure', 'Planning Opportunities'] },
  { icon: 'reconcile', title: 'Reconciliation Intelligence', tags: ['Books vs Tax', 'Inter-entity', 'Counterparties', 'Mismatches', 'Exception Management'] },
  { icon: 'users', title: 'Related-Party Intelligence', tags: ['Entity', 'Transactions', 'Balances', 'Transfer Pricing', 'Regulatory Compliance'] },
  { icon: 'consolidation', title: 'Close & Consolidation Intelligence', tags: ['Entities', 'Eliminations', 'Adjustments', 'Variance', 'Close Cycle Time'] },
  { icon: 'disclosure', title: 'Disclosure Intelligence', tags: ['Requirements', 'Changes', 'Schedules', 'Evidence', 'Traceability'] },
  { icon: 'controls', title: 'Compliance & Controls', tags: ['Controls', 'Policy', 'Exceptions', 'Approvals', 'Audit Trail'] },
];

const pnl: { label: string; value: string; tone: 'slate' | 'rose' | 'emerald' }[] = [
  { label: 'Revenue', value: '₹ 1,250 Cr', tone: 'slate' },
  { label: 'COGS', value: '(₹ 840 Cr)', tone: 'rose' },
  { label: 'Gross Profit', value: '₹ 410 Cr', tone: 'emerald' },
];

const traceSteps = [
  'Disclosure Line (Note / Schedule)',
  'Consolidation (Group)',
  'Legal Entity',
  'GL / Sub-ledger',
  'Transaction',
  'Source Document (Invoice / PO / etc.)',
  'Evidence (Contract, Approval, etc.)',
];

const actionItems: { icon: IconName; title: string; text: string; tone: string }[] = [
  { icon: 'trend', title: 'What changed?', text: 'Identify the variance or exception.', tone: 'rose' },
  { icon: 'disclosure', title: 'Which disclosure is affected?', text: 'Map to relevant notes/schedules.', tone: 'amber' },
  { icon: 'data', title: 'What data supports it?', text: 'Transactions, master data, external data.', tone: 'blue' },
  { icon: 'alert', title: 'Are there exceptions?', text: 'Analyse, quantify and understand root cause.', tone: 'indigo' },
  { icon: 'users', title: 'Who must review?', text: 'Identify stakeholders and workflow.', tone: 'purple' },
  { icon: 'check', title: 'Approve and disclose.', text: 'Complete with confidence.', tone: 'cyan' },
];
const actionFinal = { icon: 'evidence' as IconName, title: 'Retain evidence.', text: 'Be audit ready, always.' };

const problems = questions.slice(0, 7);
const problemStart = 32;

const systems = ['SAP', 'Tax', 'Group Reporting', 'Treasury', 'Procurement', 'Sales', 'Documents', 'External Data'];

/* ===================== SCROLL ANIMATION ===================== */
/* Sirf fade-up (neeche se upar) aur zoom. Left/right slide nahi.
   Teesra value = stagger ke liye kitne parent upar ka group lena hai (default 1). */
const animGroups: [string, 'fade-up' | 'zoom', number?][] = [
  ['.sec-head, .opview-head', 'fade-up'],
  ['.q-card', 'fade-up'],
  ['.flow-card', 'zoom'],
  ['.eco-card', 'zoom'],
  ['.coverage-card', 'zoom'],
  ['.cockpit', 'fade-up'],
  ['.wq-head, .stat-card', 'zoom'],
  ['.trace', 'fade-up'],
  ['.trace-step', 'fade-up', 2],
  ['.cockpit-foot', 'fade-up'],
  ['.pipeline', 'fade-up'],
  ['.pipeline-row', 'fade-up'],
  ['.ps-card', 'fade-up'],
  ['.problem-row', 'fade-up'],
  ['.sys-chip', 'zoom'],
  ['.stack-step', 'fade-up'],
  ['.final-cta-box', 'zoom'],
];

function useScrollReveal(root: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = root.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('rv--in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    animGroups.forEach(([selector, type, levels = 1]) => {
      const perParent = new Map<Element, number>();
      el.querySelectorAll<HTMLElement>(selector).forEach((node) => {
        let parent: Element = node;
        for (let l = 0; l < levels; l++) parent = parent.parentElement ?? parent;

        const i = perParent.get(parent) ?? 0;
        perParent.set(parent, i + 1);

        node.classList.add('rv', `rv--${type}`);
        node.style.setProperty('--rv-delay', `${(i % 6) * 80}ms`); // stagger
        io.observe(node);
      });
    });

    return () => io.disconnect();
  }, [root]);
}

/* ============================== PAGE ============================== */

export default function Tax() {
  const pageRef = useRef<HTMLDivElement>(null);
  useScrollReveal(pageRef);

  return (
    <div className="tax-page" ref={pageRef}>
      <main>
        {/* ============================= HERO ============================= */}
        <section className="tax-hero" data-purpose="hero-section">
          <div className="tax-hero-bg-image"></div>
          <div className="tax-hero-bg-overlay"></div>
          <div className="tax-hero-grid"></div>
          <div className="tax-hero-glow tax-hero-glow--left"></div>
          <div className="tax-hero-glow tax-hero-glow--right"></div>

          <div className="op-container op-container--left tax-hero-inner">
            <div className="tax-hero-layout">
              <div className="tax-hero-content">
                <h1 className="tax-hero-title">
                  From Every Transaction
                  <br />
                  to Every Disclosed Number.
                </h1>

                <p className="tax-hero-text">
                  Orane helps enterprises connect transactions, tax, controls, consolidation and
                  disclosure — so exceptions are identified earlier, numbers remain traceable, and
                  compliance becomes part of the operating flow.
                </p>

                <div className="tax-hero-actions">
                  <Link className="btn-primary" href="/tellusyourproblem#tell-details">
                    Tell Us Your Tax &amp; Disclosure Problem
                    <Arrow />
                  </Link>
                  <button className="btn-glass" type="button">
                    See How It Works
                    <span className="play-icon">▶</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ================== THE QUESTIONS LEADERS ASK ================== */}
        <section className="op-section op-section--white" data-purpose="leader-questions">
          <div className="op-container">
            <div className="sec-head">
              <div className="sec-badge">Real Decisions</div>
              <h2 className="sec-title">The Questions Leaders Actually Ask</h2>
              <p className="sec-sub">Real questions. Real decisions. Real impact.</p>
            </div>

            <div className="q-grid">
              {questions.map((q) => (
                <div className="q-card" key={q}>
                  <span className="q-mark">?</span>
                  <p className="q-text">{q}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= FROM TRANSACTIONS TO DISCLOSURE ================= */}
        <section className="op-section op-section--slate" data-purpose="transactions-to-disclosure">
          <div className="op-container">
            <div className="sec-head sec-head--center">
              <div className="sec-badge">Connected View</div>
              <h2 className="sec-title">From Transactions to Disclosure</h2>
              <p className="sec-sub sec-sub--base">A connected view. End-to-end. Always traceable.</p>
            </div>

            <div className="flow-grid">
              {flow.map((f, i) => (
                <div className="flow-card" key={f.name}>
                  <div className="flow-card-top">
                    <div className="flow-icon">
                      <Icon name={f.icon} />
                    </div>
                    <span className="flow-num">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="flow-title">{f.name}</h3>
                  <p className="flow-desc">{f.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ========================= BUSINESS OUTCOMES ========================= */}
        <section className="op-section op-section--white" data-purpose="business-outcomes">
          <div className="op-container">
            <div className="sec-head sec-head--center">
              <div className="sec-badge">Outcomes</div>
              <h2 className="sec-title">Business Outcomes</h2>
              <p className="sec-sub sec-sub--base">From better control to greater trust.</p>
            </div>

            <div className="eight-grid">
              {outcomes.map((o) => (
                <div className="eco-card" key={o.text}>
                  <div className="eco-icon">
                    <Icon name={o.icon} />
                  </div>
                  <h3 className="eco-title">{o.text}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ======================= INTELLIGENCE AREAS ======================= */}
        <section className="op-section op-section--slate" data-purpose="intelligence-areas">
          <div className="op-container op-container--left">
            <div className="sec-head">
              <div className="sec-badge">Coverage Matrix</div>
              <h2 className="sec-title">Tax &amp; Disclosure Intelligence Areas</h2>
              <p className="sec-sub">Focus on what matters. Solve real business problems.</p>
            </div>

            <div className="coverage-grid">
              {intelligence.map((item) => (
                <div className="coverage-card" key={item.title}>
                  <div>
                    <div className="coverage-icon">
                      <Icon name={item.icon} />
                    </div>
                    <h3 className="coverage-title">{item.title}</h3>
                    <div className="tag-list">
                      {item.tags.map((t) => (
                        <span className="tag" key={t}>{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="coverage-foot">
                    <span>Intelligence Area</span>
                    <span className="coverage-dot"></span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ======================== WORKED QUESTION ======================== */}
        <section className="op-section op-section--white" data-purpose="worked-question">
          <div className="op-container">
            <div className="opview-head">
              <div>
                <div className="sec-badge">Worked Example</div>
                <h2 className="sec-title">A Worked Question</h2>
                <p className="sec-sub sec-sub--base">
                  Follow one disclosed number all the way back to its evidence.
                </p>
              </div>
              <div className="opview-live">
                <span className="opview-live-dot"></span>
                Illustrative Data
              </div>
            </div>

            <div className="cockpit">
              <div className="cockpit-bar">
                <div className="cockpit-bar-left">
                  <div className="cockpit-light cockpit-light--red"></div>
                  <div className="cockpit-light cockpit-light--amber"></div>
                  <div className="cockpit-light cockpit-light--green"></div>
                  <span className="cockpit-name">ORANE-TAX-VIEW // DISCLOSURE TRACE</span>
                </div>
                <div className="cockpit-bar-right">
                  <span className="cockpit-chip">MODE: ILLUSTRATIVE</span>
                </div>
              </div>

              <h3 className="cockpit-question">“Can every disclosed number be traced to its source?”</h3>

              <div className="wq-grid">
                <div className="wq-left">
                  <div className="wq-head">
                    <span className="wq-head-title">Disclosed Number</span>
                    <span className="wq-head-sub">Consolidated P&amp;L</span>
                  </div>
                  {pnl.map((row) => (
                    <div className="stat-card" key={row.label}>
                      <div className="stat-card-head">
                        <span>{row.label}</span>
                        <span className={`stat-dot stat-dot--${row.tone}`}></span>
                      </div>
                      <div className={`stat-value stat-value--${row.tone}`}>{row.value}</div>
                    </div>
                  ))}
                </div>

                <div className="trace">
                  <div className="trace-head">
                    <span>Trace to source</span>
                    <span className="trace-count">{traceSteps.length} Levels</span>
                  </div>
                  {traceSteps.map((step, i) => (
                    <div key={step}>
                      <div className="trace-step">
                        <span className="trace-step-num">{i + 1}</span>
                        {step}
                      </div>
                      {i < traceSteps.length - 1 && <div className="trace-connector"></div>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="cockpit-foot">
                <div className="cockpit-foot-left">
                  <span className="trace-badge">
                    <Icon name="check" />
                    TRACEABLE
                  </span>
                  <span className="cockpit-foot-value">From Report to Source</span>
                </div>
                <div className="cockpit-foot-right">
                  <span className="queue-label">To build trust, every number must have a source.</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============== HINDSIGHT TO ACTION + 52 PROBLEMS ============== */}
        <section className="op-section op-section--slate" data-purpose="hindsight-to-action">
          <div className="op-container">
            <div className="sec-head">
              <div className="sec-badge">Decision Ladder</div>
              <h2 className="sec-title">From Hindsight to Action</h2>
              <p className="sec-sub">Turn questions into clarity and decisions.</p>
            </div>

            <div className="ladder-grid">
              <div className="pipeline">
                <h3 className="pipeline-head">
                  <span>Question to Evidence</span>
                  <span className="pipeline-count">{actionItems.length + 1} Steps</span>
                </h3>
                {actionItems.map((row) => (
                  <div className="pipeline-row pipeline-row--top" key={row.title}>
                    <div className={`pipeline-num tone-${row.tone}`}>
                      <Icon name={row.icon} />
                    </div>
                    <div className="pipeline-body">
                      <div className="pipeline-label">{row.title}</div>
                      <p className="pipeline-desc">{row.text}</p>
                    </div>
                  </div>
                ))}
                <div className="pipeline-row pipeline-row--top pipeline-row--final">
                  <div className="pipeline-num pipeline-num--final">
                    <Icon name={actionFinal.icon} />
                  </div>
                  <div className="pipeline-body">
                    <div className="pipeline-label">{actionFinal.title}</div>
                    <p className="pipeline-desc">{actionFinal.text}</p>
                  </div>
                </div>
              </div>

              <div className="ps-card">
                <div className="sec-badge">Relevant 52 Business Problems (32–38)</div>
                <h3 className="ps-title">Key Tax &amp; Disclosure Questions</h3>
                <p className="ps-sub">Explore the full library.</p>

                <div className="problem-list">
                  {problems.map((p, i) => (
                    <div className="problem-row" key={p}>
                      <span className="problem-num">{problemStart + i}</span>
                      <span className="problem-text">{p}</span>
                    </div>
                  ))}
                </div>

                <div className="ps-foot">
                  <Link className="btn-small" href="/52problem">
                    Explore All 52 Business Problems
                    <Arrow />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ======================= ENTERPRISE SYSTEMS ======================= */}
        <section className="op-section op-section--white" data-purpose="enterprise-systems">
          <div className="op-container">
            <div className="sec-head sec-head--center">
              <div className="sec-badge">Enterprise Systems &amp; Data</div>
              <h2 className="sec-title">Bring Data Together</h2>
              <p className="sec-sub sec-sub--base">
                Bring data together. Add business context. Deliver intelligence.
              </p>
            </div>

            <div className="sys-grid">
              {systems.map((s) => (
                <span className="sys-chip" key={s}>{s}</span>
              ))}
            </div>

            <div className="stack">
              <div className="stack-step">TRUSTED TRANSACTION DATA</div>
              <span className="stack-arrow">↓</span>
              <div className="stack-step">RULES + BUSINESS CONTEXT</div>
              <span className="stack-arrow">↓</span>
              <div className="stack-step stack-step--final">
                TAX / DISCLOSURE INTELLIGENCE
                <small>Exception → Review → Action → Evidence</small>
              </div>
            </div>
          </div>
        </section>

        {/* ========================= FINAL CTA ================================ */}
        <section className="final-cta" data-purpose="final-call-to-action">
          <div className="final-cta-bg"></div>
          <div className="final-cta-overlay"></div>
          <div className="op-container final-cta-inner">
            <div className="final-cta-box">
              <div className="final-cta-left">
                <h2 className="final-cta-title">Don’t Discover Compliance Problems at the End.</h2>
                <p className="final-cta-text">
                  Detect earlier. Explain clearly. Trace completely. Act before filing.
                </p>
              </div>
              <div className="final-cta-right">
                <Link className="btn-primary btn-primary--cta" href="/tellusyourproblem#tell-details">
                  Tell Us Your Tax &amp; Disclosure Problem
                  <Arrow />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}